import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import { ApiError } from '../api'
import { Link, navigate } from '../router'
import type { Catalog, LicenseConfig, Medium, Modifier, PriceTable, Priced, UsageClass } from '../shop'
import { PRESETS, eur, pctLabel, shop, useCart, useCatalog, useShopConfig } from '../shop'
import { useMe, useStored } from '../state'
import type { Picked } from './LightTable'
import { InquiryForm } from './Inquiry'

type Draft = { cls: string | null; medium: string | null; table: string | null; params: Record<string, string>; mods: Record<string, number> }
const EMPTY: Draft = { cls: null, medium: null, table: null, params: {}, mods: {} }
// category dimensions whose first value is the obvious default
const DEFAULT_FIRST = new Set(['exclusivity', 'paywall', 'territory', 'languages'])
const GROUP_LABELS: Record<string, string> = {
  blocking_territory: 'Sperrung für andere Nutzungen', domains: 'Mehrere Domains', multi_month: 'Mehrmonatiges Kalendarium',
}

function find(cat: Catalog | undefined, d: Draft) {
  const cls = cat?.classes.find((c) => c.code === d.cls)
  const medium = cls?.media.find((m) => m.code === d.medium)
  const table = medium?.tables.find((t) => t.code === d.table)
  return { cls, medium, table }
}

function locate(cat: Catalog, medium: string) {
  for (const c of cat.classes) for (const m of c.media) if (m.code === medium) return { cls: c, medium: m }
  return null
}

function withDefaults(t: PriceTable, params: Record<string, string>) {
  const out: Record<string, string> = {}
  for (const d of t.dims) {
    const keep = params[d.key] && d.values.some((v) => v.label === params[d.key])
    if (keep) out[d.key] = params[d.key]
    else if (d.values.length === 1 || DEFAULT_FIRST.has(d.key)) out[d.key] = d.values[0].label
  }
  return out
}

export function LicensePage({ id, itemId }: { id: number; itemId: number | null }) {
  const qc = useQueryClient()
  const { data: me } = useMe()
  const { data: conf } = useShopConfig()
  const catalog = useCatalog()
  const cart = useCart()
  const asset = useQuery({ queryKey: ['license-asset', id], queryFn: () => shop.asset(id), retry: false })
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const [edit, setEdit] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)
  const [added, setAdded] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [alsoPicked, setAlsoPicked] = useState(false)
  const [picked] = useStored<Picked[]>('zint-lighttable', [])
  const others = picked.filter((p) => p.id !== id)
  const inquiryRef = useRef<HTMLDivElement>(null)
  const [inquiryOpen, setInquiryOpen] = useState(false)
  const initialised = useRef(false)

  // editing a cart item: start from its configuration
  useEffect(() => {
    if (initialised.current || !catalog.data) return
    if (itemId && !cart.data) return
    initialised.current = true
    const row = itemId ? cart.data?.items.find((i) => i.id === itemId) : null
    if (row) applyConfig(row.config, catalog.data)
  }, [catalog.data, cart.data, itemId])  // eslint-disable-line react-hooks/exhaustive-deps

  function applyConfig(c: Omit<LicenseConfig, 'modifiers'> & { modifiers?: LicenseConfig['modifiers'] }, cat = catalog.data) {
    if (!cat) return
    const hit = locate(cat, c.medium)
    const table = hit?.medium.tables.find((t) => t.code === c.table)
    if (!hit || !table) return
    setDraft({ cls: hit.cls.code, medium: hit.medium.code, table: table.code, params: withDefaults(table, c.params),
               mods: Object.fromEntries((c.modifiers ?? []).map((m) => [m.code, m.units])) })
    setEdit(null)
  }

  const { cls, medium, table } = find(catalog.data, draft)
  const wire: LicenseConfig | null = medium && table ? {
    medium: medium.code, table: table.code, params: draft.params,
    modifiers: Object.entries(draft.mods).map(([code, units]) => ({ code, units })),
  } : null
  const quote = useQuery<Priced, ApiError>({
    queryKey: ['quote', wire], enabled: !!wire, retry: false, placeholderData: keepPreviousData,
    queryFn: ({ signal }) => shop.quote(wire!, signal),
  })
  const q = wire ? quote.data : undefined

  function chooseClass(c: UsageClass) {
    setDraft(c.code === draft.cls ? draft : { ...EMPTY, cls: c.code })
    setEdit(null)
  }
  function chooseMedium(m: Medium) {
    const t = m.tables.length === 1 ? m.tables[0] : null
    setDraft({ ...draft, medium: m.code, table: t?.code ?? null, params: t ? withDefaults(t, {}) : {}, mods: {} })
    setEdit(null)
  }
  function chooseTable(t: PriceTable) {
    setDraft({ ...draft, table: t.code, params: withDefaults(t, draft.params), mods: {} })
    setEdit(null)
  }
  function setParam(key: string, value: string) {
    setDraft((d) => ({ ...d, params: { ...d.params, [key]: value } }))
  }
  function toggleMod(m: Modifier, all: Modifier[]) {
    setDraft((d) => {
      const mods = { ...d.mods }
      if (mods[m.code]) {
        delete mods[m.code]
        // dropping a group member also drops options that require that group
        if (m.group) for (const x of all) if (x.requires === m.group) delete mods[x.code]
      } else {
        if (m.group) for (const x of all) if (x.group === m.group) delete mods[x.code]
        mods[m.code] = 1
      }
      return { ...d, mods }
    })
  }
  function clearGroup(group: string, all: Modifier[]) {
    setDraft((d) => {
      const mods = { ...d.mods }
      for (const x of all) if (x.group === group || x.requires === group) delete mods[x.code]
      return { ...d, mods }
    })
  }

  async function addToCart() {
    if (!wire) return
    setBusy(true)
    setError(null)
    try {
      let result
      if (itemId) {
        result = await shop.updateCart(itemId, wire)
      } else {
        result = await shop.addToCart(id, wire)
        if (alsoPicked) {
          const failed: string[] = []
          for (const p of others) {
            try { result = await shop.addToCart(p.id, wire) } catch { failed.push(p.inv) }
          }
          if (failed.length) setError(`Nicht hinzugefügt (keine Online-Lizenz möglich): ${failed.join(', ')}`)
        }
      }
      qc.setQueryData(['cart'], result)
      setAdded(itemId ? 'Lizenz im Warenkorb geändert.' : alsoPicked && others.length ? `${others.length + 1} Lizenzen im Warenkorb.` : 'Im Warenkorb.')
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Konnte nicht hinzugefügt werden.')
    } finally {
      setBusy(false)
    }
  }

  function openInquiry() {
    setInquiryOpen(true)
    setTimeout(() => inquiryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 30)
  }

  const a = asset.data
  const blocked = a && !a.can_license ? a.license_blocked : conf && !conf.enabled ? 'Online-Lizenzen sind derzeit nicht verfügbar.' : null
  const img = a?.images.preview ?? a?.images.small

  if (asset.error) {
    return (
      <main className="page wrap">
        <p className="banner">{asset.error instanceof ApiError ? asset.error.message : 'Objekt nicht gefunden.'}</p>
        <Link to="/" className="linkbtn">Zur Recherche</Link>
      </main>
    )
  }

  const step1 = !draft.cls || edit === 1
  const step2 = !!draft.cls && (!draft.medium || edit === 2)
  const multiTable = (medium?.tables.length ?? 0) > 1
  const step3 = multiTable && (!draft.table || edit === 3)
  const presets = catalog.data ? PRESETS.filter((p) => locate(catalog.data!, p.config.medium)?.medium.tables.some((t) => t.code === p.config.table)) : []

  return (
    <main className="page wrap lic">
      <div className="crumbs">
        <button className="linkbtn" onClick={() => (window.history.length > 1 ? window.history.back() : navigate('/'))}>← Zurück</button>
        {itemId && <span className="muted">Lizenz im Warenkorb bearbeiten</span>}
      </div>
      <div className="lic-grid">
        <section className="lic-main" aria-labelledby="lic-title">
          <h1 id="lic-title">Lizenz berechnen</h1>
          <p className="lead">Wählen Sie, wie Sie das Bild nutzen möchten. Der Preis folgt den Honorarempfehlungen der
            mfm (Bildhonorare 2026) und wird sofort berechnet.</p>

          {blocked ? (
            <p className="banner info">{blocked}</p>
          ) : (
            <>
              {presets.length > 0 && (
                <div className="presets" aria-label="Häufige Nutzungen">
                  <span className="label">Häufig</span>
                  {presets.map((p) => (
                    <button key={p.label} className={medium?.code === p.config.medium && table?.code === p.config.table ? 'on' : ''}
                      onClick={() => applyConfig(p.config)}>{p.label}</button>
                  ))}
                </div>
              )}

              <Step n={1} title="Wofür nutzen Sie das Bild?" open={step1} summary={cls?.label} onEdit={() => setEdit(1)}>
                {catalog.isLoading && <p className="muted">Lade Nutzungsarten …</p>}
                <div className="tiles">
                  {catalog.data?.classes.map((c) => (
                    <button key={c.code} className={`tile${c.code === draft.cls ? ' on' : ''}`} onClick={() => chooseClass(c)}>
                      <b>{c.label}</b><span>{c.description}</span>
                    </button>
                  ))}
                </div>
              </Step>

              {draft.cls && cls && (
                <Step n={2} title="In welchem Medium?" open={step2} summary={medium?.title} onEdit={() => setEdit(2)}>
                  <MediaPicker media={cls.media} value={draft.medium} onPick={chooseMedium} />
                </Step>
              )}

              {medium && multiTable && (
                <Step n={3} title="Welche Ausgabe?" open={step3} summary={table?.title} onEdit={() => setEdit(3)}>
                  <div className="optlist">
                    {medium.tables.map((t) => (
                      <button key={t.code} className={`opt${t.code === draft.table ? ' on' : ''}`} onClick={() => chooseTable(t)}>
                        <b>{t.title}</b>{t.note && <span>{t.note}</span>}
                      </button>
                    ))}
                  </div>
                </Step>
              )}

              {table && (
                <Step n={multiTable ? 4 : 3} title="Umfang der Nutzung" open>
                  {table.dims.map((d) => (
                    <fieldset key={d.key} className="dim">
                      <legend>{d.label}</legend>
                      <div className="vchips" role="radiogroup" aria-label={d.label}>
                        {d.values.map((v) => {
                          const price = q?.alternatives?.[d.key]?.[v.label]
                          const on = draft.params[d.key] === v.label
                          return (
                            <button key={v.label} role="radio" aria-checked={on} className={`vchip${on ? ' on' : ''}${v.display.length > 28 ? ' long' : ''}`}
                              onClick={() => setParam(d.key, v.label)}>
                              <span className="v">{v.display}</span>
                              <span className="p">{price === undefined ? '' : price === null ? 'auf Anfrage' : `${q?.complete ? '' : 'ab '}${eur(price)}`}</span>
                            </button>
                          )
                        })}
                      </div>
                    </fieldset>
                  ))}
                  {table.beyond && <p className="note">Über der höchsten Stufe: {table.beyond}</p>}
                </Step>
              )}

              {table && table.modifiers.length > 0 && (
                <Step n={multiTable ? 5 : 4} title="Besonderheiten" open optional>
                  <Modifiers mods={table.modifiers} medium={medium!} chosen={draft.mods}
                    onToggle={(m) => toggleMod(m, table.modifiers)} onClear={(g) => clearGroup(g, table.modifiers)}
                    onUnits={(code, n) => setDraft((d) => ({ ...d, mods: { ...d.mods, [code]: Math.max(1, Math.min(20, n)) } }))} />
                </Step>
              )}

              {medium && catalog.data && (
                <details className="terms">
                  <summary>Bedingungen dieser Lizenz</summary>
                  <ul>{[...medium.terms, ...catalog.data.general_terms].map((t) => <li key={t}>{t}</li>)}</ul>
                </details>
              )}
            </>
          )}

          <div ref={inquiryRef} className="inq-wrap" id="anfrage">
            {inquiryOpen || blocked ? (
              <InquiryForm assetIds={[id]} config={wire} summary={q?.description ?? []} me={me}
                assetLabel={a ? `${a.inv} – ${a.title}` : ''} onClose={blocked ? undefined : () => setInquiryOpen(false)} />
            ) : (
              <div className="inq-teaser">
                <div>
                  <b>Ihre Nutzung ist nicht dabei?</b>
                  <span>Exklusivrechte, Auslandsnutzung, große Auflagen, Bundles oder anderes – wir erstellen Ihnen ein Angebot.</span>
                </div>
                <button className="btn" onClick={openInquiry}>Individuell anfragen</button>
              </div>
            )}
          </div>
        </section>

        {!blocked && q?.complete && !q.by_agreement && !added && (
          <div className="lic-bar">
            <div><b>{eur(q.net_cents)}</b><span>netto</span></div>
            <button className="btn primary" onClick={addToCart} disabled={busy || quote.isFetching}>{itemId ? 'Übernehmen' : 'In den Warenkorb'}</button>
          </div>
        )}
        <aside className="lic-side" aria-label="Ihre Lizenz">
          <div className="receipt">
            <div className="rc-obj">
              <div className="rc-img">{img ? <img src={img.url} alt="" /> : <span className="muted">kein Bild</span>}</div>
              <div>
                <div className="mono muted">{a?.inv ?? '…'}</div>
                <div className="rc-title">{a?.title ?? ''}</div>
                <div className="muted small">{[a?.photographer?.label, a?.date].filter(Boolean).join(' · ')}</div>
              </div>
            </div>
            {!blocked && (
              <Receipt q={q} loading={quote.isFetching} error={wire ? quote.error : null} hasTable={!!table}
                vat={conf?.vat_rate ?? 7} />
            )}
            {!blocked && q?.complete && !q.by_agreement && (
              <>
                {!itemId && others.length > 0 && (
                  <label className="check">
                    <input type="checkbox" checked={alsoPicked} onChange={(e) => setAlsoPicked(e.target.checked)} />
                    <span>Gleiche Lizenz auch für {others.length} {others.length === 1 ? 'weiteres Bild' : 'weitere Bilder'} vom Leuchttisch</span>
                  </label>
                )}
                <button className="btn primary wide" onClick={addToCart} disabled={busy || quote.isFetching}>
                  {busy ? 'Moment …' : itemId ? 'Änderung übernehmen' : 'In den Warenkorb'}
                </button>
              </>
            )}
            {!blocked && q?.by_agreement && (
              <button className="btn primary wide" onClick={openInquiry}>Angebot anfragen</button>
            )}
            {error && <p className="err" role="alert">{error}</p>}
            {added && (
              <div className="added" role="status">
                <b>✓ {added}</b>
                <div><Link to="/" className="linkbtn">Weiter recherchieren</Link> <Link to="/warenkorb" className="btn">Zum Warenkorb ({cart.data?.count ?? 0})</Link></div>
              </div>
            )}
            {!blocked && <button className="linkbtn small" onClick={openInquiry}>Andere Nutzung anfragen</button>}
          </div>
        </aside>
      </div>
    </main>
  )
}

function Step({ n, title, open, summary, onEdit, optional, children }: {
  n: number; title: string; open: boolean; summary?: string; onEdit?: () => void; optional?: boolean; children: React.ReactNode
}) {
  return (
    <section className={`step${open ? ' open' : ' done'}`}>
      <header>
        <span className="num">{n}</span>
        <h2>{title}{optional && <small> optional</small>}</h2>
        {!open && summary && <span className="sum">{summary}</span>}
        {!open && onEdit && <button className="linkbtn" onClick={onEdit}>ändern</button>}
      </header>
      {open && <div className="step-body">{children}</div>}
    </section>
  )
}

function MediaPicker({ media, value, onPick }: { media: Medium[]; value: string | null; onPick: (m: Medium) => void }) {
  const [filter, setFilter] = useState('')
  const f = filter.trim().toLowerCase()
  const shown = f ? media.filter((m) => (m.title + ' ' + m.description).toLowerCase().includes(f)) : media
  return (
    <>
      {media.length > 8 && (
        <input className="ffilter" placeholder="Medium suchen, z. B. Buch, Plakat, Website …" value={filter}
          onChange={(e) => setFilter(e.target.value)} aria-label="Medien filtern" />
      )}
      <div className="optgrid">
        {shown.map((m) => (
          <button key={m.code} className={`opt${m.code === value ? ' on' : ''}`} onClick={() => onPick(m)} title={m.description}>
            <b>{m.title}</b><span>{m.description}</span>
          </button>
        ))}
        {shown.length === 0 && <p className="muted">Nichts gefunden. Fragen Sie Ihre Nutzung gern individuell an.</p>}
      </div>
    </>
  )
}

function Modifiers({ mods, medium, chosen, onToggle, onClear, onUnits }: {
  mods: Modifier[]; medium: Medium; chosen: Record<string, number>
  onToggle: (m: Modifier) => void; onClear: (group: string) => void; onUnits: (code: string, n: number) => void
}) {
  const sections = useMemo(() => {
    const own = mods.filter((m) => !m.global)
    const global = mods.filter((m) => m.global)
    return [{ title: `Speziell für ${medium.title}`, items: own }, { title: 'Sperrungen und Rabatte', items: global }].filter((s) => s.items.length)
  }, [mods, medium.title])
  const groupsChosen = new Set(mods.filter((m) => chosen[m.code] && m.group).map((m) => m.group))

  return (
    <div className="mods">
      {sections.map((s) => {
        const seen = new Set<string>()
        return (
          <div key={s.title} className="modsec">
            <div className="label">{s.title}</div>
            {s.items.map((m) => {
              if (m.group) {
                if (seen.has(m.group)) return null
                seen.add(m.group)
                const members = s.items.filter((x) => x.group === m.group)
                const prefix = commonPrefix(members.map((x) => x.label))
                return (
                  <div key={m.group} className="mgroup" role="radiogroup" aria-label={GROUP_LABELS[m.group] ?? 'Auswahl'}>
                    <span className="gl">{GROUP_LABELS[m.group] ?? (prefix ? prefix.replace(/:\s*$/, '') : 'Eine davon')}</span>
                    <button className={`mchip${!groupsChosen.has(m.group) ? ' on' : ''}`} role="radio" aria-checked={!groupsChosen.has(m.group)}
                      onClick={() => onClear(m.group!)}>keine</button>
                    {members.map((x) => <ModChip key={x.code} m={x} on={!!chosen[x.code]} onToggle={onToggle} radio short={prefix ? x.label.slice(prefix.length) : undefined} />)}
                  </div>
                )
              }
              const disabled = !!m.requires && !groupsChosen.has(m.requires)
              return (
                <div key={m.code} className="mline">
                  <ModChip m={m} on={!!chosen[m.code]} onToggle={onToggle} disabled={disabled}
                    hint={disabled ? `Setzt »${GROUP_LABELS[m.requires!] ?? m.requires}« voraus` : undefined} />
                  {chosen[m.code] && m.per_unit && (
                    <span className="stepper" aria-label="Anzahl">
                      <button onClick={() => onUnits(m.code, chosen[m.code] - 1)} aria-label="weniger">−</button>
                      <span className="mono">{chosen[m.code]}×</span>
                      <button onClick={() => onUnits(m.code, chosen[m.code] + 1)} aria-label="mehr">+</button>
                    </span>
                  )}
                </div>
              )
            })}
          </div>
        )
      })}
    </div>
  )
}

/** "Sperrung …: deutschsprachig", "Sperrung …: europaweit" -> "Sperrung …: " */
function commonPrefix(labels: string[]) {
  if (labels.length < 2) return ''
  let p = labels[0]
  for (const l of labels) while (!l.startsWith(p)) p = p.slice(0, -1)
  const cut = p.lastIndexOf(': ')
  return cut > 0 ? p.slice(0, cut + 2) : ''
}

function ModChip({ m, on, onToggle, radio, disabled, hint, short }: { m: Modifier; on: boolean; onToggle: (m: Modifier) => void; radio?: boolean; disabled?: boolean; hint?: string; short?: string }) {
  return (
    <button className={`mchip${on ? ' on' : ''}${m.kind === 'by_agreement' ? ' ask' : ''}`} onClick={() => onToggle(m)} disabled={disabled}
      role={radio ? 'radio' : 'checkbox'} aria-checked={on} title={hint ?? (short ? m.label : undefined)}>
      <span>{short ?? m.label}</span>
      <span className={`pct${m.kind === 'discount' ? ' minus' : ''}`}>{pctLabel(m)}{m.per_unit ? ' je Intervall' : ''}</span>
    </button>
  )
}

function Receipt({ q, loading, error, hasTable, vat }: { q: Priced | undefined; loading: boolean; error: ApiError | null; hasTable: boolean; vat: number }) {
  if (!hasTable) return <p className="rc-empty">Wählen Sie links Nutzung und Medium – der Preis erscheint hier.</p>
  if (error) return <p className="err">{error.message}</p>
  if (!q) return <p className="rc-empty">{loading ? 'Berechne …' : ''}</p>
  if (!q.complete) {
    return (
      <div className={`rc-price${loading ? ' busy' : ''}`}>
        <div className="rc-total"><span>ab</span><b>{eur(q.from_cents)}</b></div>
        <p className="muted small">netto zzgl. {vat.toLocaleString('de-DE')} % USt. · noch zu wählen: {q.missing?.join(', ')}</p>
      </div>
    )
  }
  if (q.by_agreement) {
    return (
      <div className="rc-price">
        <div className="rc-total"><b>Preis auf Anfrage</b></div>
        {q.notes.map((n) => <p key={n} className="muted small">{n}</p>)}
      </div>
    )
  }
  const vatCents = Math.round((q.net_cents ?? 0) * vat / 100)
  return (
    <div className={`rc-price${loading ? ' busy' : ''}`}>
      <ul className="rc-desc">{q.description.map((l) => <li key={l}>{l}</li>)}</ul>
      <table className="rc-lines">
        <tbody>
          <tr><td>Honorar (mfm 2026)</td><td>{eur(q.base_cents)}</td></tr>
          {q.lines.map((l) => (
            <tr key={l.label}><td>{l.label}{l.pct != null && <span className="muted"> {l.pct > 0 ? '+' : ''}{l.pct.toLocaleString('de-DE')} %</span>}</td><td>{eur(l.cents)}</td></tr>
          ))}
          {q.fee_cents > 0 && <tr><td>{q.fee_label}</td><td>{eur(q.fee_cents)}</td></tr>}
        </tbody>
      </table>
      <div className="rc-total"><span>netto</span><b>{eur(q.net_cents)}</b></div>
      <p className="muted small">zzgl. {vat.toLocaleString('de-DE')} % USt. ({eur(vatCents)}) · {eur((q.net_cents ?? 0) + vatCents)} brutto</p>
      {q.notes.map((n) => <p key={n} className="muted small">{n}</p>)}
    </div>
  )
}
